import { ComponentConfig } from "@measured/puck";
import { Image as ImageComponent } from "./Image";
import { ImageProps } from "./types";
import { MediaManagerField } from "../../fields/MediaManagerField";

export type { ImageProps } from "./types";

const ImageConfig: ComponentConfig<ImageProps> = {
    label: "Image",
    fields: {
        src: {
            type: "custom",
            label: "Image URL",
            render: ({ name, onChange, value }: { name: string; onChange: (value: string) => void; value: string }) => (
                <MediaManagerField
                    value={value}
                    onChange={onChange}
                    label="Image URL"
                    allowedMimeTypes={["image/*"]}
                    allowedExtensions={["jpg", "jpeg", "png", "gif", "webp", "svg"]}
                    placeholder="Enter image URL or select from media library"
                />
            ),
        },
        alt: {
            type: "text",
            label: "Alt Text",
            placeholder: "Enter alt text",
        },
        caption: {
            type: "text",
            label: "Caption (optional)",
            placeholder: "Enter caption",
        },
        url: {
            type: "text",
            label: "Link URL (optional)",
            placeholder: "Enter URL to make image clickable",
        },
        target: {
            type: "radio",
            label: "Link Target",
            options: [
                { label: "Same Tab", value: "_self" },
                { label: "New Tab", value: "_blank" },
            ],
        },
        hoverEffect: {
            type: "radio",
            label: "Hover Effect",
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
        },
        width: {
            type: "text",
            label: "Width",
        },
        height: {
            type: "text",
            label: "Height",
        },
        objectFit: {
            type: "radio",
            label: "Object Fit",
            options: [
                { label: "Contain", value: "contain" },
                { label: "Cover", value: "cover" },
                { label: "Fill", value: "fill" },
                { label: "None", value: "none" },
                { label: "Scale Down", value: "scale-down" },
            ],
        },
        alignment: {
            type: "radio",
            label: "Alignment",
            options: [
                { label: "Left", value: "left" },
                { label: "Center", value: "center" },
                { label: "Right", value: "right" },
            ],
        },
        borderRadius: {
            type: "text",
            label: "Border Radius",
        },
        shadow: {
            type: "radio",
            label: "Shadow",
            options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
        },
        priority: {
            type: "radio",
            label: "Priority (LCP)",
            options: [
                { label: "No", value: false },
                { label: "Yes", value: true },
            ],
        },
        loading: {
            type: "radio",
            label: "Loading",
            options: [
                { label: "Lazy", value: "lazy" },
                { label: "Eager", value: "eager" },
            ],
        },
        decoding: {
            type: "radio",
            label: "Decoding",
            options: [
                { label: "Async", value: "async" },
                { label: "Auto", value: "auto" },
            ],
        },
        sizes: {
            type: "text",
            label: "Sizes",
            placeholder: "e.g. 100vw or (min-width: 768px) 50vw, 100vw",
        },
        srcSet: {
            type: "text",
            label: "SrcSet",
            placeholder: "url-400.jpg 400w, url-800.jpg 800w",
        },
    },
    defaultProps: {
        src: "https://placehold.co/300x300",
        alt: "",
        width: "300px",
        height: "300px",
        objectFit: "cover",
        alignment: "center",
        borderRadius: "8px",
        shadow: false,
        url: "",
        target: "_blank",
        hoverEffect: true,
        priority: false,
        loading: "lazy",
        decoding: "async",
        sizes: "100vw",
    },
    render: (props: ImageProps) => <ImageComponent {...props} />,
};

export default ImageConfig;
